import { CONSTANTS, COOKIES } from '@js/global'
import { randomArrayElement, randomNumber } from '@js/utils'
import { AssetManager } from '@js/loaders'
import { CaptchaOptions, DisplayCaptchaOptions } from '@js/types'
import css from '@css/sass/styles.module.scss'
import { BaseBox, BaseBoxOptions } from './BaseBox'


export class Captcha extends BaseBox {
  private code_array: string[]
  private bodyText: HTMLHeadingElement | null = null
  private isDisplayed: boolean = false
  private bgImg: string |undefined

  constructor(options: CaptchaOptions) {
    const baseOptions: BaseBoxOptions = {
      id: options.data.id,
      title: options.data.title.toUpperCase(),
      width: CONSTANTS.css.captchaWidth,
      heightOpen: CONSTANTS.css.captchaHeight,
      bgColor: 'black',
      resizable: false,
    }
    super(baseOptions)

    options.object = this // Store Captcha objects in global COMPONENTS.captcha.object

    const assets = AssetManager.getInstance()
    this.bgImg = assets.get('motifs', 'captcha.gif')

    this.code_array = options.data.codes
  }

  protected createContent(): void {
    if (!this.box) return

    // Background image
    const bodyImg = document.createElement('img')
    this.bodyContainer?.appendChild(bodyImg)
    if (this.bgImg) bodyImg.src = this.bgImg

    // Input field
    const textInput = document.createElement('input')
    textInput.type = 'text'
    textInput.id = 'captcha_input'
    textInput.placeholder = 'Tappez le code ici'
    textInput.disabled = false
    this.bodyContainer?.appendChild(textInput)

    // Buttons
    const btnReload = document.createElement('button')
    this.bodyContainer?.appendChild(btnReload)
    btnReload.id = 'captcha_reload'
    btnReload.className = css.btn
    btnReload.innerHTML = 'Actualiser'

    const btnSubmit = document.createElement('button')
    this.bodyContainer?.appendChild(btnSubmit)
    btnSubmit.id = 'captcha_submit'
    btnSubmit.className = css.btn
    btnSubmit.innerHTML = 'Connexion'


    // Generate initial code
    this.generateRandomCode()

    console.debug('[Captcha] Content created')
  }

  // -------------------------------------------------------------------------
  //  Core logic
  // -------------------------------------------------------------------------

  updateText(): string {
    const text = `Pour entrer dans le salon privé, merci de rentrer le code suivant: ${COOKIES.captchaCode}`
    if (this.bodyText) {
      this.bodyText.innerHTML = text
    }
    return text
  }

  generateRandomCode(): void {
    COOKIES.captchaCode =
      randomArrayElement(this.code_array) + randomNumber(20, 100)
    console.log('[Captcha] code:', COOKIES.captchaCode)
    this.updateText()
  }

  // -------------------------------------------------------------------------
  //  Display / animation
  // -------------------------------------------------------------------------

  display(options: DisplayCaptchaOptions = {}): void {
    const delay = (options.delay ?? 1.5) * 1000
    const onConnect = options.onConnect ?? (() => {})
    const onRefresh = options.onRefresh ?? (() => {})
    const notif = options.notif ?? (() => 'notif')

    if (!this.isDisplayed && this.box) {
      // Make sure it's visible and positioned
      this.box.style.opacity = '0'
      this.box.style.display = 'block'

      // Optional: random position only on first display
      if (!this.isDisplayed) {
        this.setupRandomPosition()
      }

      setTimeout(() => {
        if (this.box) {
          this.box.style.transition = 'opacity 0.6s ease'
          this.box.style.opacity = '1'
        }
      }, delay)

      this.setupCaptchaListener(onConnect, onRefresh, notif)
      this.isDisplayed = true
    }
  }

  // -------------------------------------------------------------------------
  //  Event listeners
  // -------------------------------------------------------------------------

  private setupCaptchaListener(
    onConnect: () => void,
    onRefresh: (notif: any) => void,
    notif: () => any
  ): void {
    const submitBtn = document.getElementById('captcha_submit')
    const inputEl = document.getElementById('captcha_input') as HTMLInputElement | null
    const reloadBtn = document.getElementById('captcha_reload')

    const sendMessage = (inputEl: HTMLInputElement) => {
      const val = inputEl.value.toLowerCase().replace(' ', '')
      if (val === COOKIES.captchaCode || val === 'fripouille') {
        onConnect()
        // this.setClose() // Optional: hide captcha after success
      } else {
        inputEl.style.backgroundColor = '#ff0000'
        setTimeout(() => inputEl.style.backgroundColor = '', 800)
      }
    }

    if (submitBtn && inputEl) {
      // send on click button submit
      submitBtn.addEventListener('click', () => {
        sendMessage(inputEl)
      })
      // send on enter
      inputEl.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault()
          sendMessage(inputEl)
        }
      })
    }

    if (reloadBtn) {
      reloadBtn.addEventListener('click', () => {
        this.generateRandomCode()
        onRefresh(notif())
      })
    }
  }

  // -------------------------------------------------------------------------
  //  Optional override – customize minimize behavior if needed
  // -------------------------------------------------------------------------

  protected onOpenChanged(isOpen: boolean): void {
    // You can add extra behavior here, e.g. disable input when closed
    const input = document.getElementById('captcha_input') as HTMLInputElement | null
    if (input) {
      input.disabled = !isOpen
    }

    // Call super if you added extra logic in BaseBox
    // super.onOpenChanged?.(isOpen)
  }

  // -------------------------------------------------------------------------
  //  Cleanup (optional but recommended)
  // -------------------------------------------------------------------------

  destroy(): void {
    // Remove event listeners if you want to be extra clean
    const submitBtn = document.getElementById('captcha_submit')
    const reloadBtn = document.getElementById('captcha_reload')
    submitBtn?.replaceWith(submitBtn.cloneNode(true))
    reloadBtn?.replaceWith(reloadBtn.cloneNode(true))

    super.destroy()
  }
}